import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { fetchTerms } from '../api.js';
import type { Segment } from '../state/session.js';
import { useSessionStore } from '../state/session.js';
import { LevelMeter, type LevelSource } from '../components/LevelMeter.js';
import type { ConnectionState } from '../ws/client.js';
import { ReportDialog } from '../components/ReportDialog.js';
import { buildTermMatcher, TermText, type TermMatcher } from '../terms/highlight.js';
import { TermPopup } from '../terms/TermPopup.js';

const CONNECTION_LABEL: Record<ConnectionState, string> = {
  idle: '待機中',
  connecting: '接続しています',
  open: '接続中',
  reconnecting: '再接続しています',
  closed: '切断されました',
};

export function Translating({
  levelSource,
  onStop,
  onQuestion,
  onPhoto,
  wakeLockSupported,
}: {
  levelSource: LevelSource;
  onStop: () => void;
  /** 質問モード(日→中)を開く。実習中に講師へ質問するための逆方向通訳。 */
  onQuestion: () => void;
  /** 資料モード(写真翻訳)を開く。 */
  onPhoto: () => void;
  wakeLockSupported: boolean;
}): JSX.Element {
  const segments = useSessionStore((s) => s.segments);
  const connection = useSessionStore((s) => s.connection);
  const startedAtMs = useSessionStore((s) => s.startedAtMs);
  const errorMessage = useSessionStore((s) => s.errorMessage);
  const dismissError = useSessionStore((s) => s.dismissError);
  const phraseCount = useSessionStore((s) => s.phraseCount);
  const truncatedCount = useSessionStore((s) => s.truncatedCount);
  const setHoldTrim = useSessionStore((s) => s.setHoldTrim);

  // 初期は小さめ(現地フィードバック: 30px は大きすぎて一覧性が悪い)。±ボタンで調整可。
  const [fontSize, setFontSize] = useState(22);
  const [reporting, setReporting] = useState<Segment | undefined>();
  const [autoScroll, setAutoScroll] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  /**
   * ユーザー操作(タッチ/ホイール)に由来するスクロールとみなす期限。
   * iOS の慣性スクロールは指を離した後も続くため、操作時刻だけで判定すると
   * 「指を離して流し読みで上に戻った」が捕捉できず、次の字幕確定で
   * 最下部へ引き戻される(現地報告)。scroll イベントが続く限り期限を
   * 延長することで、慣性の終着点まで判定対象に含める。
   */
  const gestureChainUntilRef = useRef(0);
  /** 自動スクロール由来の scroll イベントを読み飛ばすためのフラグ。 */
  const programmaticScrollRef = useRef(false);

  // 用語ハイライト: 辞書の見出し語(日→中)を1度だけ取得してマッチャを作る。
  // 取得に失敗してもハイライトが無いだけで、字幕表示には影響させない。
  const [matcher, setMatcher] = useState<TermMatcher>({ regex: undefined, terms: {} });
  const [termPopup, setTermPopup] = useState<{ ja: string; zh: string } | undefined>();
  useEffect(() => {
    fetchTerms()
      .then((data) => setMatcher(buildTermMatcher(data.terms)))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--subtitle-size', `${fontSize}px`);
  }, [fontSize]);

  // 経過時間の表示を1秒ごとに進める。字幕の更新とは独立して動かす必要がある
  // (講師が黙っている間も時間は進むため)。
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (startedAtMs === undefined) return;
    const timer = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [startedAtMs]);

  /**
   * 自動スクロール。
   * 利用者が過去を読み返している最中に引き戻さないよう、
   * 手動で上へスクロールしたら自動追従を止める。
   */
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    // 自分で発生させたスクロールは判定に使わない。
    if (programmaticScrollRef.current) {
      programmaticScrollRef.current = false;
      return;
    }
    // ユーザー操作(タッチ/ホイール)に紐づかない scroll イベントは、
    // 字幕の差し替え・一括表示によるレイアウト変化由来なので無視する。
    // これを見てしまうと「上に戻って読んでいる最中に強制的に最下部へ引き戻される」
    // (現地報告)が起きる。
    const now = Date.now();
    if (now > gestureChainUntilRef.current) return;
    // 慣性スクロール中は scroll イベントが連続する。イベントが続く限り
    // 期限を延ばし、指を離した後の到達位置も「ユーザーの意思」として扱う。
    gestureChainUntilRef.current = now + 400;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAutoScroll(distanceFromBottom < 80);
  }, []);

  const markUserGesture = useCallback(() => {
    gestureChainUntilRef.current = Date.now() + 1000;
  }, []);

  useEffect(() => {
    if (!autoScroll) return;
    const el = listRef.current;
    if (el) {
      programmaticScrollRef.current = true;
      el.scrollTop = el.scrollHeight;
    }
  }, [segments, autoScroll]);

  // 読み返し中は古い字幕の間引きを保留する。間引くと先頭行が消えて
  // その高さぶん画面がズレ、「読んでいる最中に勝手にスクロールする」ため。
  useEffect(() => {
    setHoldTrim(!autoScroll);
    return () => setHoldTrim(false);
  }, [autoScroll, setHoldTrim]);

  const jumpToLatest = (): void => {
    const el = listRef.current;
    if (el) {
      programmaticScrollRef.current = true;
      el.scrollTop = el.scrollHeight;
    }
    setAutoScroll(true);
  };

  const confirmStop = (): void => {
    // 誤操作で講義の途中に止まると復帰に手間がかかるため、必ず確認する。
    if (window.confirm('翻訳を停止しますか？')) onStop();
  };

  return (
    <div className="translating">
      <div className="status-bar">
        <span className={`badge ${connection}`}>{CONNECTION_LABEL[connection]}</span>
        <LevelMeter source={levelSource} />
        <span className="elapsed">{formatElapsed(startedAtMs)}</span>
      </div>

      {errorMessage && (
        <div className="notice error" style={{ margin: '10px 12px 0' }}>
          {errorMessage}
          <button
            style={{ marginLeft: 10, minHeight: 32, padding: '4px 10px', fontSize: 12 }}
            onClick={dismissError}
          >
            閉じる
          </button>
        </div>
      )}

      {truncatedCount > 0 && (
        <div className="notice" style={{ margin: '10px 12px 0' }}>
          用語集が上限を超えたため {truncatedCount} 語を使用していません(使用中 {phraseCount} 語)。
          選択する用語集を減らすか、管理者に上限の引き上げを依頼してください。
        </div>
      )}

      {!wakeLockSupported && (
        <div className="notice" style={{ margin: '10px 12px 0' }}>
          このブラウザでは画面の自動消灯を防げません。端末の設定で自動ロックを長めにしてください。
        </div>
      )}

      <div
        className="subtitles"
        ref={listRef}
        onScroll={handleScroll}
        onTouchStart={markUserGesture}
        onTouchMove={markUserGesture}
        onWheel={markUserGesture}
      >
        {segments.length >= 200 && (
          <div className="reconnect-divider">これより前の字幕は「講義メモ」に保存されています</div>
        )}
        {segments.length === 0 ? (
          <div className="empty">
            講師が話し始めると、ここに字幕が表示されます。
            <br />
            上の音量バーが動いていることを確認してください。
          </div>
        ) : (
          segments.map((segment) => (
            <SegmentView
              key={segment.segmentId}
              segment={segment}
              matcher={matcher}
              onTerm={(ja, zh) => setTermPopup({ ja, zh })}
              onReport={setReporting}
            />
          ))
        )}
        {!autoScroll && (
          <button className="jump-latest" onClick={jumpToLatest}>
            最新の字幕へ
          </button>
        )}
      </div>

      <div className="bottom-bar">
        <button onClick={() => setFontSize((s) => Math.max(18, s - 4))} aria-label="文字を小さく">
          文字 −
        </button>
        <button onClick={() => setFontSize((s) => Math.min(60, s + 4))} aria-label="文字を大きく">
          文字 ＋
        </button>
        <button onClick={onQuestion} aria-label="会話モード(日本語から中国語)">
          🙋 会話
        </button>
        <button onClick={onPhoto} aria-label="資料の写真を翻訳">
          📷 資料
        </button>
        <button className="danger stop" onClick={confirmStop}>
          停止
        </button>
      </div>

      {termPopup && (
        <TermPopup ja={termPopup.ja} zh={termPopup.zh} onClose={() => setTermPopup(undefined)} />
      )}

      {reporting && (
        <ReportDialog segment={reporting} onClose={() => setReporting(undefined)} />
      )}
    </div>
  );
}

/**
 * メモ化必須: 長時間講義では数百件並ぶ。メモ化しないと、1つの partial 更新で
 * 全件が再描画され、フリーズの主因になる(segment 参照が変わった行だけ描画し直す)。
 */
const SegmentView = memo(function SegmentView({
  segment,
  matcher,
  onTerm,
  onReport,
}: {
  segment: Segment;
  matcher: TermMatcher;
  onTerm: (ja: string, zh: string) => void;
  onReport: (segment: Segment) => void;
}): JSX.Element {
  return (
    <>
      {segment.afterReconnect && <div className="reconnect-divider">— 再接続しました —</div>}
      {segment.afterBackground && (
        <div className="reconnect-divider">— この間の音声は翻訳されていません(画面を離れていました)—</div>
      )}
      <div className="segment">
        <div className={`zh ${segment.sourceFinal ? '' : 'tentative'}`}>{segment.sourceZh}</div>
        <div className={`ja ${segment.translationFinal ? '' : 'tentative'}`}>
          {segment.translationFinal ? (
            <TermText text={segment.translatedJa} matcher={matcher} onTerm={onTerm} />
          ) : (
            segment.translatedJa
          )}
        </div>
        {segment.translationFinal && (
          <button className="report-button" onClick={() => onReport(segment)}>
            誤訳を報告
          </button>
        )}
      </div>
    </>
  );
});

function formatElapsed(startedAtMs: number | undefined): string {
  if (!startedAtMs) return '--:--';
  const total = Math.floor((Date.now() - startedAtMs) / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
